import React, { useEffect, useRef, useState } from 'react';
import { Pencil, MapPin, X, Construction } from 'lucide-react';
import { flyToAndShowPin, showGeofence, removeGeofence } from '@/pages/mapview/MapCommon';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import { Input, Label, Textarea, Combobox, Button } from '@/components';
import { useERSContext } from '@/context';

const SEVERITY_OPTIONS = [
  { value: 4, label: 'Critical', dotColor: 'bg-icon-error' },
  { value: 3, label: 'High', dotColor: 'bg-chart-2' },
  { value: 2, label: 'Medium', dotColor: 'bg-chart-4' },
  { value: 1, label: 'Low', dotColor: 'bg-interactive' },
  { value: 0, label: 'Minimal', dotColor: 'bg-icon-muted' },
];

const STATUS_OPTIONS = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

export default function GeofenceItem({
  g,
  checkedGeofenceIds,
  setCheckedGeofenceIds,
  editableGeofenceIds = [],
  setEditableGeofenceIds,
  map,
  mapDraw,
  mapLinesObj,
  geofences,
  setGeofences,
  alertReceivers,
}) {
  const { postMessageToPopMapWindow, editingGeofenceFromPopMap } = useERSContext();
  const [editingG, setEditingG] = useState(JSON.parse(JSON.stringify(g)));
  const gRef = useRef(g);
  const isEditing = editableGeofenceIds.includes(g.id);

  useEffect(() => {
    gRef.current = g;
  }, [g]);

  async function deleteGeofence() {
    setEditableGeofenceIds([]);
    try {
      await wbSdk.mapsService.deleteGeofence(g.id);
    } catch (err) {
      console.error('Failed to delete geofence', err);
    }
  }

  async function updateGeofence() {
    const { updatedAt, updatedBy, isNew, ...otherOriginal } = g;
    const newGeofence = {
      id: editingG.id,
      displayName: editingG.displayName,
      description: editingG.description || '',
      severity: parseInt(editingG.severity) || 1,
      proximity: parseInt(editingG.proximity) || 0,
      active: !!editingG.active,
      color: editingG.color || '#000000',
      effected_users: editingG.effected_users || [],
      coordinates: editingG.coordinates || [],
      dynamicGroups: editingG.dynamicGroups || false,
      alert_on_enter: !!editingG.alert_on_enter,
      alert_on_leave: !!editingG.alert_on_leave,
      alarm: editingG.alarm ?? true,
      receivers: alertReceivers,
      editable: editingG.editable ?? true,
      updatedAt: Math.floor(Date.now() / 1000),
    };
    if (g.isNew) {
      setGeofences(prev => prev.filter(p => p.id !== g.id));
      delete newGeofence.updatedAt;
      delete otherOriginal.id;
      try {
        await wbSdk.mapsService.createGeofence({ ...otherOriginal, ...newGeofence });
      } catch (err) {
        console.error('Failed to create geofence', err);
      }
    } else {
      setGeofences(prev => prev.map(a => (a.id === g.id ? { ...a, ...newGeofence } : a)));
      setCheckedGeofenceIds(prev => (prev.includes(g.id) ? prev : [...prev, g.id]));
      try {
        await wbSdk.mapsService.editGeofence({ ...otherOriginal, ...newGeofence });
      } catch (err) {
        console.error('Failed to update geofence', err);
      }
    }
    setEditableGeofenceIds([]);
  }

  const flyTo = () => {
    const ring = editingG.coordinates?.[0];
    if (ring?.length > 0) {
      const [lng, lat] = ring[0];
      const currentZoom = map.getZoom();
      const targetZoom = currentZoom < 14 ? 14 : currentZoom;
      flyToAndShowPin(lat, lng, targetZoom, map, postMessageToPopMapWindow);
    }
  };

  const handleDrawUpdate = React.useCallback(
    e => {
      const updated = e.features?.[0];
      if (updated?.id === g.id && updated.geometry?.type === 'Polygon') {
        const coords = updated.geometry.coordinates;
        setEditingG(prev => ({ ...prev, coordinates: coords }));
      }
    },
    [g.id]
  );

  function updateEditingPolygon(id, polygon) {
    if (!id) return;
    mapDraw.add({
      id,
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: polygon },
      properties: {},
    });
  }

  useEffect(() => {
    if (editingG?.id !== editingGeofenceFromPopMap.id) return;
    updateEditingPolygon(editingGeofenceFromPopMap.id, editingGeofenceFromPopMap.coordinates);
    setEditingG(prev => ({ ...prev, coordinates: editingGeofenceFromPopMap.coordinates }));
  }, [editingGeofenceFromPopMap]);

  useEffect(() => {
    if (setEditableGeofenceIds) {
      if (editableGeofenceIds.includes(g.id)) {
        updateEditingPolygon(editingG?.id, editingG.coordinates);
        mapDraw.changeMode('direct_select', { featureId: editingG?.id });
        return () => {
          map.off('draw.update', handleDrawUpdate);
        };
      } else {
        try {
          mapDraw.delete(g.id);
        } catch (e) {
          console.warn(`Could not remove draw feature for geofence ${g.id}`, e);
        }
      }
    }
  }, [editableGeofenceIds, g.id]);

  useEffect(() => {
    postMessageToPopMapWindow({ type: 'editingG', payload: editingG });
  }, [editingG]);
  useEffect(() => {
    postMessageToPopMapWindow({ type: 'editableGeofenceIds', payload: editableGeofenceIds });
  }, [editableGeofenceIds]);

  useEffect(() => {
    if (checkedGeofenceIds.includes(g.id)) {
      showGeofence({ item: g, map, mapLinesObj, funcPostToPopMap: postMessageToPopMapWindow });
    } else {
      removeGeofence?.({ id: g.id, map, mapLinesObj, funcPostToPopMap: postMessageToPopMapWindow });
    }
  }, [checkedGeofenceIds, g]);

  useEffect(() => {
    if (g.isNew) enableEdit();
  }, [g.id]);

  function enableEdit() {
    const ring = gRef.current.coordinates?.[0];
    if (ring?.length > 0) {
      const [totalLng, totalLat] = ring.reduce(([sumLng, sumLat], [lng, lat]) => [sumLng + lng, sumLat + lat], [0, 0]);
      const centerLng = totalLng / ring.length;
      const centerLat = totalLat / ring.length;
      setEditingG(prev => ({ ...prev, coordinates: gRef.current.coordinates }));
      flyToAndShowPin(centerLat, centerLng, 15, map, postMessageToPopMapWindow);
    } else {
      const center = map.getCenter();
      const { lng, lat } = center;
      const offset = 0.004;
      const square = [
        [lng - offset, lat - offset],
        [lng + offset, lat - offset],
        [lng + offset, lat + offset],
        [lng - offset, lat + offset],
        [lng - offset, lat - offset],
      ];
      const coords = [square];
      setEditingG(prev => ({ ...prev, coordinates: coords }));
      flyToAndShowPin(lat, lng, 15, map, postMessageToPopMapWindow);
    }

    setEditableGeofenceIds([g.id]);
    setCheckedGeofenceIds(prev => prev.filter(id => id !== g.id));
    removeGeofence({ id: g.id, map, mapLinesObj, funcPostToPopMap: postMessageToPopMapWindow });
    map.off('draw.update', handleDrawUpdate);
    map.on('draw.update', handleDrawUpdate);
  }

  useEffect(() => {
    return () => {
      const stillExists = geofences.some(x => x.id === g.id);
      if (!stillExists) {
        removeGeofence({ id: g.id, map, mapLinesObj, funcPostToPopMap: postMessageToPopMapWindow });
      }
    };
  }, [geofences, g.id]);

  useEffect(() => {
    return () => {
      if (setEditableGeofenceIds && mapDraw) {
        mapDraw.delete(g.id);
      }
    };
  }, []);

  const handleCancel = () => {
    setEditingG(JSON.parse(JSON.stringify(g)));
    setEditableGeofenceIds([]);
    if (g.isNew) {
      setGeofences(prev => prev.filter(item => item.id !== g.id));
    }
  };

  const severityOption = SEVERITY_OPTIONS.find(s => s.value === (g.severity ?? 1));

  // Edit Mode
  if (isEditing) {
    return (
      <div className="flex flex-col rounded-xl border border-border bg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between bg-subtle border-b border-border px-3 py-2">
          <div className="flex items-center gap-2">
            <Construction size={20} className="text-muted" />
            <span className="font-medium text-sm">{g.isNew ? 'Create Geofence' : 'Edit Geofence'}</span>
          </div>
          <button onClick={handleCancel} className="p-1 hover:bg-strong rounded transition-colors" aria-label="Close">
            <X size={18} className="text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 p-3">
          {/* Geofence Name */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">
              Geofence Name <span className="text-error">*</span>
            </Label>
            <Input
              value={editingG.displayName}
              onChange={e => setEditingG(prev => ({ ...prev, displayName: e.target.value }))}
              placeholder="Enter Geofence Name"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">Description</Label>
            <Textarea
              value={editingG.description || ''}
              onChange={e => setEditingG(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description of the geofence"
              className="min-h-[60px]"
            />
          </div>

          {/* Severity */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">
              Severity <span className="text-error">*</span>
            </Label>
            <Combobox
              data={SEVERITY_OPTIONS}
              placeholder="Select Severity"
              selectedData={SEVERITY_OPTIONS.find(s => s.value === editingG.severity)}
              onChange={item => setEditingG(prev => ({ ...prev, severity: item?.value ?? 1 }))}
            />
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">
              Status <span className="text-error">*</span>
            </Label>
            <Combobox
              data={STATUS_OPTIONS}
              placeholder="Select Status"
              selectedData={STATUS_OPTIONS.find(s => s.value === editingG.active)}
              onChange={item => setEditingG(prev => ({ ...prev, active: item?.value ?? true }))}
            />
          </div>

          {/* Alerts */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm">Alerts</Label>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox
                  checked={editingG.alert_on_enter}
                  onCheckedChange={checked => setEditingG(prev => ({ ...prev, alert_on_enter: checked }))}
                />
                <span className="text-sm">On Entry</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox
                  checked={editingG.alert_on_leave}
                  onCheckedChange={checked => setEditingG(prev => ({ ...prev, alert_on_leave: checked }))}
                />
                <span className="text-sm">On Exit</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox
                  checked={editingG.alarm}
                  onCheckedChange={checked => setEditingG(prev => ({ ...prev, alarm: checked }))}
                />
                <span className="text-sm">Trigger Alarm</span>
              </label>
            </div>
          </div>

          {/* Dynamic Group Chat */}
          <div className="flex flex-col gap-1.5">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={editingG.dynamicGroups}
                onCheckedChange={checked => setEditingG(prev => ({ ...prev, dynamicGroups: checked }))}
              />
              <span className="text-sm">Enable Dynamic Group Chat</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          {!g.isNew && (
            <Button variant="destructive" size="sm" onClick={deleteGeofence}>
              Delete
            </Button>
          )}
          <Button variant="default" size="sm" onClick={updateGeofence}>
            {g.isNew ? 'Complete' : 'Update'}
          </Button>
        </div>
      </div>
    );
  }

  // View Mode
  return (
    <div className="flex items-start gap-3 py-2 px-3 rounded-lg border border-border bg-subtle">
      {/* Checkbox */}
      <div className="pt-1">
        <Checkbox
          checked={checkedGeofenceIds.includes(g.id)}
          onCheckedChange={isChecked => {
            setCheckedGeofenceIds(prev => (isChecked ? [...prev, g.id] : prev.filter(id => id !== g.id)));
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-medium text-sm truncate">{g.displayName}</span>
          {severityOption && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${severityOption.dotColor} text-white`}>
              {severityOption.label}
            </span>
          )}
        </div>
        <div className="text-xs text-muted space-y-1">
          <div className="flex items-center gap-2">
            <span className={g.active ? 'text-success' : 'text-muted'}>{g.active ? 'Active' : 'Inactive'}</span>
            <span>·</span>
            <span>
              Alerts: {g.alert_on_enter && 'Entry'}
              {g.alert_on_enter && g.alert_on_leave && ', '}
              {g.alert_on_leave && 'Exit'}
              {!g.alert_on_enter && !g.alert_on_leave && 'None'}
            </span>
          </div>
          {g.description && <p className="truncate">{g.description}</p>}
          <p>
            {g.createdBy || '—'} ·{' '}
            {g.creationTimestamp ? new Date(g.creationTimestamp * 1000).toLocaleDateString() : '—'}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 pt-0.5">
        {setEditableGeofenceIds && (
          <button
            onClick={enableEdit}
            className="p-1.5 hover:bg-strong rounded transition-colors"
            aria-label="Edit geofence"
          >
            <Pencil size={16} className="text-muted hover:text-interactive" />
          </button>
        )}
        <button onClick={flyTo} className="p-1.5 hover:bg-strong rounded transition-colors" aria-label="Go to geofence">
          <MapPin size={16} className="text-muted hover:text-interactive" />
        </button>
      </div>
    </div>
  );
}
